/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Button } from "@mui/material";
import { useState } from "react";
import PageContainer from "../page-container/page-container";
import { useNotification } from "../../context/notification-provider";
import LoadingScreen from "../progress/loading-screen";
import { JunkValue } from "./types";
import useJunkValues from "./use-watch-list";
import getColumns from "./data-grid";
import JunkValueDelete from "./delete-dialog";
import AddJunkValueDialog from "./add-dialog";

const JunkValues = () => {
  const { showNotification } = useNotification();

  const [selectedJunkValue, setSelectedJunkValue] = useState<JunkValue | null>(
    null,
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const { data: junkValues, isLoading, error, mutate } = useJunkValues();

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: `Refreshing junk values data...`,
      severity: "info",
      duration: 2000,
    });
    mutate();
  }, [showNotification, mutate]);

  const handleOnDelete = React.useCallback(
    async (selectedElement: JunkValue) => {
      setSelectedJunkValue(selectedElement);
    },
    [],
  );

  const handleOnDeleteClose = () => {
    setSelectedJunkValue(null);
    mutate();
  };

  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading junk values data.
      </Alert>
    );
  }

  return (
    <PageContainer
      title="Junk Values"
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleOnRefreshClick}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleAddClick}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading junk values data.
          </Alert>
        ) : (
          <DataGrid
            rows={junkValues || []}
            columns={getColumns({
              onDelete: handleOnDelete,
            })}
            disableRowSelectionOnClick
            loading={isLoading}
            showToolbar
            sx={{
              height: "560px",

              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: "transparent",
              },
              [`& .${gridClasses.columnHeader}`]: {
                backgroundColor: "#f5f6fa",
                outline: "transparent",
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.row}:hover`]: {
                cursor: "pointer",
              },
              "& .row-disabled": {
                opacity: 0.5,
                pointerEvents: "none",
              },
            }}
            slotProps={{
              loadingOverlay: {
                variant: "circular-progress",
                noRowsVariant: "circular-progress",
              },
              baseIconButton: {
                size: "small",
              },
            }}
          />
        )}
      </Box>
      {selectedJunkValue && (
        <JunkValueDelete
          open={Boolean(selectedJunkValue)}
          selectedJunkValue={selectedJunkValue}
          onClose={handleOnDeleteClose}
        />
      )}
      {isAddDialogOpen && (
        <AddJunkValueDialog
          open={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
        />
      )}
    </PageContainer>
  );
};
export default JunkValues;
